import { getAPIErrorMessage } from '@gonfalon/errors/getAPIErrorMessage';
import { RESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { getClient } from '@gonfalon/openapi/client';
import { getAccount } from '@gonfalon/openapi/operations/getAccount';
import { getSamlConfig } from '@gonfalon/openapi/operations/getSamlConfig';
import { getScimConfig } from '@gonfalon/openapi/operations/getScimConfig';
import { getVersions } from '@gonfalon/openapi/operations/getVersions';
import { internalGetUnverifiedMemberPendingInvites } from '@gonfalon/openapi/operations/internalGetUnverifiedMemberPendingInvites';
import { internalGetUnverifiedMemberPendingInvitesCookie } from '@gonfalon/openapi/operations/internalGetUnverifiedMemberPendingInvitesCookie';
import {
  internalPostCompleteUnverifiedMemberSignupCookie,
  type InternalPostCompleteUnverifiedMemberSignupCookieRequestBody,
} from '@gonfalon/openapi/operations/internalPostCompleteUnverifiedMemberSignupCookie';
import { patchAccount } from '@gonfalon/openapi/operations/patchAccount';
import { postCreateUnverifiedMember } from '@gonfalon/openapi/operations/postCreateUnverifiedMember';
import { type PostVerifyCodeRequestBody } from '@gonfalon/openapi/operations/postVerifyCode';
import type { CompleteUnverifiedMemberSignupRep } from '@gonfalon/openapi/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

export type VerifyEmailCodeInput = {
  body: PostVerifyCodeRequestBody;
  referer?: string | null;
  signupSource?: string | null;
};

async function postVerifyCodeWithSignupContext({ body, referer, signupSource }: VerifyEmailCodeInput) {
  const params = new URLSearchParams();
  if (referer) {
    params.set('referer', referer);
  }
  if (signupSource) {
    params.set('signup_source', signupSource);
  }

  const query = params.toString();
  const path = '/internal/account/verify-code' as const;
  const url = query ? `${path}?${query}` : path;
  const client = getClient();
  const postRequest = client.POST.bind(client);

  return postRequest(url as '/internal/account/verify-code', {
    params: {},
    body,
  });
}

export const getAccountQuery = createQueryOptions(getAccount);
export const useUpdateAccount = createMutationHook(patchAccount, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getAccountQuery.partialQueryKey() });
  },
});
export const getSamlConfigQuery = createQueryOptions(getSamlConfig);
export const getScimConfigQuery = createQueryOptions(getScimConfig);

export const useCreateUnverifiedMember = createMutationHook(postCreateUnverifiedMember, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getAccountQuery.partialQueryKey() });
  },
});

export const useVerifyEmailCode = createMutationHook(postVerifyCodeWithSignupContext);

export const getPendingInvitesQuery = createQueryOptions(internalGetUnverifiedMemberPendingInvites);

export const getPendingInvitesWithCookieQuery = createQueryOptions(internalGetUnverifiedMemberPendingInvitesCookie);

const COMPLETE_SIGNUP_SUCCESS_STATUSES = new Set([201, 202, 204]);

/**
 * Cookie-based signup completion. Returns the raw fetch response so trial-limit
 * redirects can be handled by the caller (fetch follows 303 automatically).
 */
export async function postCompleteUnverifiedMemberSignupWithCookie(
  body: InternalPostCompleteUnverifiedMemberSignupCookieRequestBody,
): Promise<{ destination?: CompleteUnverifiedMemberSignupRep['destination']; response: Response }> {
  const { data, response, error } = await internalPostCompleteUnverifiedMemberSignupCookie({ body });

  if (response.redirected && response.url.includes('trial-limit')) {
    return { response };
  }

  if (!COMPLETE_SIGNUP_SUCCESS_STATUSES.has(response.status)) {
    const code =
      error !== null && typeof error === 'object' && 'code' in error && typeof error.code === 'string'
        ? error.code
        : 'unknown_error';
    throw new RESTAPIError({
      status: response.status,
      url: response.url,
      code,
      message: getAPIErrorMessage(error, `Failed to complete signup (${response.status})`),
    });
  }

  let destination: CompleteUnverifiedMemberSignupRep['destination'] = 'sdk-setup';
  if (response.status === 201) {
    destination = data?.destination ?? 'sdk-setup';
  }

  return { destination, response };
}

export const getAPIVersionsQuery = createQueryOptions(getVersions);
